// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TySeveritySegment = {
    key: string;
    label: string;
    value: number;
    tone: "good" | "warn" | "bad";
};

type Props = {
    segments: TySeveritySegment[];
    emptyLabel: string;
    /** Makes the strip a control: clicking a band filters the list down to it. */
    onSelect?: (key: string) => void;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The whole picture in one line: what share of traffic succeeded, was refused, and actually broke.
 * A single proportional strip answers "is anything wrong" before the eye reaches a number, which a
 * column of separate counts cannot do.
 *
 * Drawn as SVG rather than flex widths because the proportions are data, and this repo keeps inline
 * style out of the layout layer — same reasoning as `MeterList`.
 */
export default function SeverityBar({ segments, emptyLabel, onSelect }: Props) {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    if (!total) return <p className="severity-bar-empty">{emptyLabel}</p>;

    const shown = segments.filter((segment) => segment.value > 0);

    // Each band starts where everything before it ended, so the run is summed rather than carried in
    // a mutable counter — the same result, without writing to a variable mid-render.
    const bands = shown.map((segment, index) => ({
        ...segment,
        x: (shown.slice(0, index).reduce((sum, earlier) => sum + earlier.value, 0) / total) * 100,
        width: (segment.value / total) * 100,
    }));

    return (
        <div className="severity-bar">
            <svg className="severity-bar-track" viewBox="0 0 100 10" preserveAspectRatio="none" role="img">
                {bands.map((band) => (
                    <rect
                        key={band.key}
                        className="severity-bar-segment"
                        data-tone={band.tone}
                        data-clickable={onSelect ? "true" : undefined}
                        x={band.x}
                        y="0"
                        width={band.width}
                        height="10"
                        onClick={onSelect ? () => onSelect(band.key) : undefined}
                    >
                        <title>{`${band.label}: ${band.value.toLocaleString()}`}</title>
                    </rect>
                ))}
            </svg>

            <div className="severity-bar-legend">
                {shown.map((segment) => (
                    <span key={segment.key} className="severity-bar-legend-item">
                        <span className="severity-bar-dot" data-tone={segment.tone} />
                        {segment.label}
                        <strong>{segment.value.toLocaleString()}</strong>
                        <span className="severity-bar-pct">{Math.round((segment.value / total) * 100)}%</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
